<!--TODOS: Needs validation box? -->
<template>
  <li
    :class="[
      'owc-header-navigation-topic',
      isTopicPartOfDesktopNavigation ? 'owc-header-navigation-topic--desktop-nav' : '',
      isModelsFlyout ? 'owc-header-navigation-topic__model-flyout' : '',
      isEditMode ? 'owc-header-author' : '',
    ]"
    role="none"
  >
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <button
      class="owc-header-navigation-topic__button"
      @click="openFlyout"
      aria-haspopup="true"
      :aria-expanded="shouldShowInnerFlyout"
      role="menuitem"
      :isIconSmall="false"
    >
      <wb-icon class="owc-header-navigation-topic__desktop-models-icon" aria-hidden="true" :name="iconName" />
      <p class="owc-header-navigation-topic__label">
        {{ navigationTopicLabel }}
      </p>
      <div class="owc-header-navigation-topic__icon-wrapper">
        <wb-icon class="wb-round-button__icon" aria-hidden="true" name="bds/chevron-right/16" flip-rtl />
      </div>
    </button>
    <HeaderFlyout
      v-if="shouldShowInnerFlyout || isEditMode"
      :isFirstLevelFlyout="false"
      :flyoutTitle="navigationTopicLabel"
      :currentFlyoutLevel="nextFlyoutLevel"
      :isFlyoutNavVisible="isFlyoutNavVisible"
      :flyoutId="innerComponentId"
      :flyoutInnerItems="innerFlyoutItems"
      :isAuthorEditMode="isEditMode"
      @closeFlyout="closeFlyout"
      ref="flyout"
    >
      <slot v-if="isEditMode" />
      <slot slot="seamless-vmos-flyout" name="seamless-vmos-flyout"></slot>
    </HeaderFlyout>
  </li>
</template>

<script lang="ts">
import { HeaderNavigationTopicComponent } from '@/packages/Header/src/HeaderNavigationTopic';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import headerMixin from '@owc-utils/Mixins/Header';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { FlyoutLevels } from '@owc-packages/Header/src/Header.enums';

import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import HeaderFlyout from '@owc-packages/Header/src/HeaderFlyout.vue';
import OwcHeaderButton from '@owc-packages/Header/src/HeaderButton.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { AEM_AUTHOR_CLASS, HEADER_MAIN_NAV_FLYOUT } from '@owc-packages/Header/src/Header.configs';
import { buildHeaderComponentId } from './HeaderUtils';
import { updateActiveFlyout } from '@owc-utils/Seamless/headerIntegrations';
import { HeaderState } from '@owc-types/Header';

export default (ComponentInitializeDispatcher as HeaderNavigationTopicComponent).extend({
  name: 'owc-header-navigation-topic',
  mixins: [headerMixin, OwcDataComponent],
  components: {
    SlotContainer,
    HeaderFlyout,
    OwcHeaderButton,
    ValidationBoxWrapper,
  },
  mounted() {
    this.innerComponentId = this.getComponentId();

    if (!this.innerComponentId) {
      this.innerComponentId = buildHeaderComponentId(
        'topic',
        this.navigationTopicLabel.trim(),
        this.componentData?.listIndex,
      );
    }

    this.storeStateChangeListener = this.onHeaderStateChange(state => {
      if (this.isEditMode) {
        this.$el.classList.add(AEM_AUTHOR_CLASS);
      } else {
        this.openFlyoutIfPartOfChain(state);
      }
    });

    this.openFlyoutIfPartOfChain();
  },
  beforeDestroy() {
    if (this.storeStateChangeListener) {
      this.removeOnHeaderStateChange(this.storeStateChangeListener);
    }
  },
  data() {
    return {
      shouldShowInnerFlyout: false,
      isFlyoutNavVisible: true,
      innerComponentId: '',
    };
  },
  computed: {
    navigationTopicLabel() {
      if (this.componentData) {
        return this.componentData.label;
      }
      return '';
    },
    analyticsId() {
      if (this.componentData) {
        return this.componentData.analyticsId || this.componentData.label;
      }
      return '';
    },
    currentFlyoutLevel() {
      if (this.componentData?.currentFlyoutLevel) {
        return this.componentData?.currentFlyoutLevel;
      }

      return FlyoutLevels.FLYOUT_LEVEL_ONE;
    },
    nextFlyoutLevel() {
      if (this.currentFlyoutLevel) {
        return this.currentFlyoutLevel + 1;
      }

      return FlyoutLevels.FLYOUT_LEVEL_TWO;
    },
    innerFlyoutItems() {
      if (this.componentData && this.componentData.items) {
        return [...this.componentData.items];
      }

      return [];
    },
    isTopicPartOfDesktopNavigation() {
      return Boolean(this.componentData?.isTopicPartOfDesktopNavigation);
    },
    //TODO: This logic might no longer be necessary if this component will no longer be a WC
    isModelsFlyout() {
      const componentId = this.getComponentId();

      if (componentId) {
        return [this.getModelTopicId()].includes(this.getComponentId());
      } else {
        return this.componentData?.listIndex === 0 && this.isTopicPartOfDesktopNavigation;
      }
    },
    iconName() {
      const businessUnit = window?.aemNamespace?.pageEnvironmentVariables?.businessUnit;
      if (businessUnit === 'vans' || businessUnit === 'camper-vans') {
        return 'bds/van-frontal/24';
      } else return 'bds/car/24';
    },
  },
  methods: {
    openFlyout() {
      if (this.shouldShowInnerFlyout) {
        this.closeFlyout(this.isTopicPartOfDesktopNavigation);
      } else {
        const openFlyoutChain = [];

        updateActiveFlyout(this.$refs.flyout, HEADER_MAIN_NAV_FLYOUT, true);
        this.setOpenFlyoutId(this.innerComponentId);

        if (this.isTopicPartOfDesktopNavigation) {
          openFlyoutChain.push({
            infoToAdd: HEADER_MAIN_NAV_FLYOUT,
            flyoutLevel: FlyoutLevels.FLYOUT_LEVEL_ONE,
          });
        }
        openFlyoutChain.push({ infoToAdd: this.innerComponentId, flyoutLevel: this.nextFlyoutLevel });

        this.openHeaderFlyout(openFlyoutChain);
        this.addToTrackingTree(this.analyticsId, this.nextFlyoutLevel);
      }
    },
    closeFlyout(isGlobalClose: boolean) {
      updateActiveFlyout(this.$refs.flyout, HEADER_MAIN_NAV_FLYOUT, false);

      this.closeHeaderFlyout(isGlobalClose, true);
    },
    openFlyoutIfPartOfChain(storeState: HeaderState) {
      const state = storeState || this.getHeaderStoreState();
      const isModelsFlyoutOpen = state.modelsFlyoutOpen;

      this.shouldShowInnerFlyout = this.isThisFlyoutOpen(state.flyoutChain, this.innerComponentId);
      this.isFlyoutNavVisible = !(this.isModelsFlyout && isModelsFlyoutOpen);

      if (!this.shouldShowInnerFlyout) {
        this.$el.classList.remove('owc-header-navigation-topic--selected');
      } else {
        this.$el.classList.add('owc-header-navigation-topic--selected');
      }
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils';
@import './styles/flyout-vars';

.owc-header-navigation-topic {
  @include breakpoint-to(mq4) {
    @include wb-content-width;
  }

  margin-bottom: $flyout-spacing-extra-small;

  @include breakpoint-from(mq5) {
    padding: 0;
  }

  .owc-header-navigation-topic {
    &__button {
      @extend %button-normalize;

      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;

      width: 100%;
      min-height: 64px - $flyout-spacing-extra-small;

      margin: 0;

      text-align: left;

      list-style-type: none;

      @include breakpoint-from(mq5) {
        min-height: 40px;

        margin-bottom: $flyout-spacing-extra-small;
      }
    }
    &__label {
      @include wb-type-copy;

      position: relative;

      justify-content: flex-start;

      padding-right: 0;

      color: var(--wb-grey-20);

      cursor: pointer;
    }
    &__icon-wrapper {
      width: $button-clickable-area;
      height: $button-clickable-area;

      color: var(--wb-black);

      @include breakpoint-from(mq3) {
        color: var(--wb-grey-20);

        &:hover {
          background-color: var(--wb-white);
        }
      }
    }
    &__label,
    &__icon-wrapper {
      display: flex;

      align-items: center;
      justify-content: center;
    }
    &__contents {
      display: none;
    }
    &__desktop-models-icon {
      display: none;
    }
    &:last-of-type {
      margin-bottom: $flyout-spacing;
    }
  }
  &--desktop-nav {
    margin-bottom: 0;

    @include breakpoint-from(mq5) {
      & > .owc-header-navigation-topic {
        &__button {
          position: relative;

          height: 64px;

          margin-bottom: 0;
          padding: $flyout-spacing-small;

          .owc-header-navigation-topic__label {
            @include wb-type-copy;

            color: var(--wb-grey-70);
          }
          .owc-header-navigation-topic__icon-wrapper {
            display: none;
          }
          &:after {
            transform: scaleX(0);

            transition: transform 100ms ease-in-out;

            content: '';
          }
          &:hover {
            .owc-header-navigation-topic__label {
              color: var(--wb-white);
            }
          }
        }
      }
      &.owc-header-navigation-topic__model-flyout > .owc-header-navigation-topic__button {
        .owc-header-navigation-topic__desktop-models-icon {
          display: flex;

          align-items: center;

          width: $button-inner-icon-size--large;
          height: $button-inner-icon-size--large;

          margin-right: 8px;

          color: var(--wb-grey-70);
        }
        &:hover {
          .owc-header-navigation-topic__desktop-models-icon {
            color: var(--wb-white);
          }
        }
      }
    }
  }
  &--selected {
    @include breakpoint-from(mq5) {
      & > .owc-header-navigation-topic {
        &__icon-wrapper {
          transform: translateX(20px);
        }
      }
      &:not(.owc-header-navigation-topic--desktop-nav) {
        .owc-header-navigation-topic__label {
          font-weight: bold;
        }
      }
      &.owc-header-navigation-topic--desktop-nav {
        & > .owc-header-navigation-topic__button {
          .owc-header-navigation-topic__label {
            color: var(--wb-white);
          }
          .owc-header-navigation-topic__desktop-models-icon {
            color: var(--wb-white);
          }
          &:after {
            @include breakpoint-from(mq5) {
              position: absolute;
              top: 0;
              left: 0;

              width: 100%;
              height: 1px;

              background: var(--wb-blue-45);

              transform: scaleX(1);
              transform-origin: 0 50%;

              animation-timing-function: ease-out;

              content: '';
            }
          }
        }
      }
    }
  }

  @include rtl-rule('.owc-header-navigation-topic', false) {
    &__button {
      text-align: right;
    }
  }
}
</style>

<!--AUTHOR STYLES-->
<style lang="scss">
$this: '.owc-header-navigation-topic';

#{$this}.owc-header-author {
  .owc-header-flyout {
    position: relative;

    width: 100%;
    height: fit-content;

    border: 1px dashed var(--wb-grey-70);
    overflow: visible;

    .owc-header-flyout__topbar {
      background-color: var(--wb-grey-70);
    }
    &.owc-header-flyout-intercept::before {
      height: 0;

      pointer-events: none;
    }
  }
  .owc-header-flyout.owc-header-flyout__last-level {
    left: 0;

    width: 100%;
  }
}
#{$this}.owc-header-author,
#{$this}.owc-header-navigation-topic--desktop-nav.owc-header-author {
  #{$this}__button {
    display: none;
  }
}
</style>
